<script setup>
import AssignmentRuleForm from "@/components/helpdesk/assignment_rules/AssignmentRuleForm.vue";
import AssignmentRuleDataTable from "@/components/helpdesk/assignment_rules/AssignmentRuleDataTable.vue";
import { useNavigationStore } from "@/stores/navigation"
import { onMounted, ref, watch } from "vue";
import axios from "@/utils/axios";
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import { useLoading } from "vue-loading-overlay";
import loaderData from "@/utils/loader-data";
import DeleteConfirm from "@/components/common/DeleteConfirm.vue";

const $loading = useLoading(loaderData);
const navigationStore = useNavigationStore()
const errors = ref([]);
const headers = ref([
  {
    title: "Name",
    key: "name",
    sortable: true,
  },
  {
    title: "Description",
    key: "description",
    sortable: true,
  },
  {
    title: "Form",
    key: "form",
    sortable: true,
  },
  {
    title: "Status",
    key: "status",
    sortable: true,
  },
  {
    title: "Actions",
    key: "actions",
    sortable: false,
  },
]);

const assignment_rules = ref([]);
const forms = ref([]);
const forms_complete = ref([]);
const form_field_rules = ref([]);
const users = ref([]);
const teams = ref([]);
const roles = ref([]);

const openModal = ref(false);
const deleteDialog = ref(false);
const isCanSeeCreateBtn = ref(false);
const modeModal = ref("create");

const filterInfo = ref({});
const editedItem = ref({});

const getAssignmentRules = async (payload = null) => {
  let url = `/helpdesk/assignment_rules?page=${payload.page}&per_page=${payload.perPage}`;
  if (payload.searchQuery && payload.searchQuery.trim() !== "") {
    url += `&search_query=${encodeURIComponent(payload.searchQuery.trim())}`;
  }
  if (payload.sortBy && payload.orderBy) {
    url += `&sort_by=${payload.sortBy}&order_by=${payload.orderBy}`;
  }
  let loader = $loading.show();
  axios
    .get(url)
    .then((response) => {
      if (response.status == "success") {
        assignment_rules.value = response.data.assignment_rules;
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        });
      }
    })
    .catch((error) => {
      loader.hide();
    })
    .finally(() => {
      loader.hide();
    });
};

const ticketAssignmentRulesByFieldType = (field_type) => {
  axios
    .get(`/helpdesk/ticket_assignment_rules`)
    .then((response) => {
      if (response.status == "success") {
        form_field_rules.value = response.data.ticket_assignment_rules;
      }
    })
    .catch((error) => {})
    .finally(() => {});
};

const getUsers = (search_query = "") => {
  axios
    .get(`/get-all-users`)
    .then((response) => {
      if (response.status === "success") {
        users.value = Object.entries(response.data.users).map(
          ([index, value]) => {
            return {
              value: value.id,
              title: value.first_name + " " + value.last_name,
            };
          }
        );
      }
    })
    .catch((error) => {})
    .finally(() => {});
};


const getRoles = (search_query = "") => {
  axios
    .get("get-all-roles")
    .then(async (response) => {
      if (response.status == "success") {
        roles.value = Object.entries(response.data.roles).map(
          ([index, value]) => {
            return {
              value: index,
              title: value,
            };
          }
        );
      }
    })
    .catch((error) => {})
    .finally(() => {});
};

const getTeams = (search_query = "") => {
  axios
    .get("get-all-teams")
    .then(async (response) => {
      if (response.status == "success") {
        teams.value = Object.entries(response.data.teams).map(
          ([index, value]) => {
            return {
              value: value.id,
              title: value.name,
            };
          }
        );
      }
    })
    .catch((error) => {})
    .finally(() => {});
};


const getForms = (search_query = "") => {
  axios
    .get(`/helpdesk/forms-list?page=assignment_rules`)
    .then((response) => {
      if (response.status === "success") {
        forms_complete.value = response.data.forms;
        forms.value = response.data.forms.map((contact) => {
          return {
            title: contact.name,
            value: contact.id,
          };
        });
      }
    })
    .catch((error) => {})
    .finally(() => {});
};

const canSeeCreateBtn = async () => {
  const userData = JSON.parse(localStorage.getItem('userData'));
  const userPermissionsByRoles = userData.permissions_by_roles;
  isCanSeeCreateBtn.value = Object.values(userPermissionsByRoles).some(
    (permissions) => permissions.includes("Create Assignment Rule")
  );
};

const openCreateModel = async () => {
  modeModal.value = "create";
  editedItem.value = {};
  openModal.value = true;
};

// Page changed from pagination
const tableChanged = async (payload) => {
  filterInfo.value = { ...filterInfo.value, ...payload };
  getAssignmentRules(filterInfo.value);
};

const searchValue = async (payload) => {
  filterInfo.value.searchQuery = payload;
  getAssignmentRules(filterInfo.value);
};

const refreshAssignmentRules = async () => {
  getAssignmentRules(filterInfo.value);
};

const editItem = (item) => {
  const { form, form_field, assigned_to, ...newItem } = item;
  let users = item.assigned_to.users.filter(user => true);
  let teams = item.assigned_to.teams.filter(team => true);
  let roles = item.assigned_to.roles.filter(role => true);
  editedItem.value = {
    ...newItem,
    is_active: Number(item.is_active),
    form_id: Number(item.form_id),
    form_field_id: Number(item.form_field_id),
    run_on_create: item.run_on_create == 1,
    run_on_update: item.run_on_update == 1,
    assigned_to: {
      users: users,
      teams: teams,
      roles: roles,
    }
  };

  modeModal.value = "Update";
  openModal.value = true;
};

const deleteItem = (item) => {
  editedItem.value.id = item.id;
  deleteDialog.value = true;
};

const deleteItemConfirm = async () => {
  axios
    .delete("/helpdesk/assignment_rules/" + `${editedItem.value.id}`)
    .then((response) => {
      if (response.status == "success") {
        toast.success(response.message, {
          autoClose: 2000,
        });
        deleteDialog.value = false;
        // refresh the users list via API
        getAssignmentRules(filterInfo.value);
      } else {
        toast.error(response.message, {
          autoClose: 2000,
        });
      }
    })
    .catch((error) => {})
    .finally(() => {});
};

// load contacts when page loads
onMounted(async () => {
  navigationStore.setRouteActiveName("helpdesk-forms")
  console.log('navigationStore', navigationStore);

  canSeeCreateBtn();
  getUsers();
  getTeams();
  getRoles();
  getForms();
  ticketAssignmentRulesByFieldType();
});

// expose errors and getContacts
defineExpose({
  errors,
  getAssignmentRules,
});

definePage({ meta: { requiresAuth: true } });
</script>

<template>
  <VCard title="" class="">
    <VCardText class="p-0" style="padding: 0 !important">
      <AssignmentRuleDataTable
        :headers="headers"
        :table-data="assignment_rules"
        :canSeeCreateBtn="isCanSeeCreateBtn"
        @tableChanged="tableChanged"
        @searchValue="searchValue"
        @isDialogVisible="openCreateModel"
        @editItem="editItem"
        @deleteItem="deleteItem"
      />
    </VCardText>
  </VCard>

  <AssignmentRuleForm
    v-model:isDialogVisible="openModal"
    :form-data="editedItem"
    :forms="forms"
    :users="users"
    :teams="teams"
    :roles="roles"
    :form_field_rules="form_field_rules"
    :forms_complete="forms_complete"
    :mode="modeModal"
    @refreshAssignmentRules="refreshAssignmentRules"
  />

  <DeleteConfirm
    v-model:deleteDialog="deleteDialog"
    @closeDelete="deleteDialog = false"
    @deleteItemConfirm="deleteItemConfirm"
  />
</template>
